import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheck,
  faPhone,
  faCommentDots,
  faCloudUploadAlt,
  faBed,
  faBath,
  faCalendarAlt,
  faCoins,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import foundHouseIcon from '../assets/foundhouse.png';
import searchingIcon from '../assets/searching.png';

import { FieldLabel, ChoiceCard, LocationPicker } from './TeamUpFormControls';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

import { lifestyleOptions } from '../constants/lifestyle';
import { desiredTeamSizeOptions, currentTeamSizeOptions } from '../constants/teamSize';
import { bedroomsOptions, bathroomsOptions } from '../constants/flatRooms';
import { genderOptions } from '../constants/gender';

// Generic click-to-open picker for a short fixed list of options, styled
// like the city/region selects. Self-contained (manages its own open/close
// + outside-click), value/label pairs are localized via getLocalizedText.
function OptionPicker({ value, options, onSelect, getLocalizedText, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="tu-select-wrapper" ref={wrapperRef} onClick={() => setIsOpen(!isOpen)}>
      <div className="tu-select-display">
        <span>{selectedOption ? getLocalizedText(selectedOption.ar, selectedOption.en) : placeholder}</span>
        <FontAwesomeIcon icon={faChevronDown} className="tu-select-icon" />
      </div>
      {isOpen && (
        <div className="tu-dropdown-menu">
          {options.map((opt) => (
            <div
              key={opt.value}
              className="tu-dropdown-item"
              onClick={(e) => { e.stopPropagation(); onSelect(opt.value); setIsOpen(false); }}
            >
              {getLocalizedText(opt.ar, opt.en)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const AGE_OPTIONS = Array.from({ length: 65 }, (_, i) => 18 + i); // 18 → 82

// Simple click-to-open numeric picker for Age, styled like the city/region
// selects but self-contained (manages its own open/close + outside-click).
function AgePicker({ value, onSelect, getLocalizedText }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="tu-select-wrapper" ref={wrapperRef} onClick={() => setIsOpen(!isOpen)}>
      <div className="tu-select-display">
        <span>{value || getLocalizedText('اختر عمرك', 'Select your age')}</span>
        <FontAwesomeIcon icon={faChevronDown} className="tu-select-icon" />
      </div>
      {isOpen && (
        <div className="tu-dropdown-menu">
          {AGE_OPTIONS.map((ageValue) => (
            <div
              key={ageValue}
              className="tu-dropdown-item"
              onClick={(e) => { e.stopPropagation(); onSelect(String(ageValue)); setIsOpen(false); }}
            >
              {ageValue}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The earliest selectable move-in date is tomorrow — the user isn't allowed
// to pick today or any past date. Computed once when the module loads.
function getTomorrow() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
}
const MIN_MOVE_IN_DATE = getTomorrow();

// Small calendar popup used for move-in date fields. Styled like the other
// click-to-open selects (tu-select-wrapper / tu-dropdown-menu), but renders
// a month grid instead of a list. Dates before `minDate` are disabled.
function DateField({ value, onSelect, getLocalizedText, minDate = MIN_MOVE_IN_DATE }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const base = value ? new Date(`${value}T00:00:00`) : minDate;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const locale = getLocalizedText('ar-EG', 'en-US');
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const monthLabel = viewDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDayIndex).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const isDisabled = (day) => new Date(year, month, day) < minDate;
  const isSelected = (day) => {
    if (!value) return false;
    const sel = new Date(`${value}T00:00:00`);
    return sel.getFullYear() === year && sel.getMonth() === month && sel.getDate() === day;
  };

  const handlePick = (day) => {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onSelect(iso);
    setIsOpen(false);
  };

  const displayValue = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
    : getLocalizedText('اختر التاريخ', 'Select a date');

  return (
    <div className="tu-select-wrapper" ref={wrapperRef}>
      <div className="tu-select-display" onClick={() => setIsOpen(!isOpen)}>
        <span>{displayValue}</span>
        <FontAwesomeIcon icon={faCalendarAlt} className="tu-select-icon" />
      </div>
      {isOpen && (
        <div className="tu-dropdown-menu tu-calendar-popup" onClick={(e) => e.stopPropagation()}>
          <div className="tu-calendar-header">
            <button type="button" className="tu-calendar-nav" onClick={() => setViewDate(new Date(year, month - 1, 1))}>
              <FontAwesomeIcon icon={getLocalizedText(faChevronRight, faChevronLeft)} />
            </button>
            <span className="tu-calendar-month-label">{monthLabel}</span>
            <button type="button" className="tu-calendar-nav" onClick={() => setViewDate(new Date(year, month + 1, 1))}>
              <FontAwesomeIcon icon={getLocalizedText(faChevronLeft, faChevronRight)} />
            </button>
          </div>
          <div className="tu-calendar-grid">
            {cells.map((day, i) =>
              day === null ? (
                <span key={i} className="tu-calendar-cell tu-calendar-cell-empty" />
              ) : (
                <button
                  type="button"
                  key={i}
                  className={`tu-calendar-cell ${isSelected(day) ? 'is-selected' : ''}`}
                  disabled={isDisabled(day)}
                  onClick={() => handlePick(day)}
                >
                  {day}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AboutStep(p) {
  const { formData, getLocalizedText, isDragging, setIsDragging, handleDrop, handlePhotoChange,
    handleRemovePhoto, fileInputRef, compressionStatus, isFieldValid, touchedSteps, handleInput, setField } = p;

  return (
    <div className="tu-step-body">
      <div className="tu-field-row">
        <div className="tu-field">
          <FieldLabel valid={isFieldValid('first_name')} touched={touchedSteps[0]}>
            {getLocalizedText('الاسم الأول', 'First Name')}
          </FieldLabel>
          <input
            type="text"
            name="first_name"
            className="tu-input"
            value={formData.first_name}
            onChange={handleInput}
            maxLength={11}
            placeholder={getLocalizedText('الاسم الأول', 'First name')}
          />
        </div>
        <div className="tu-field">
          <FieldLabel valid={isFieldValid('second_name')} touched={touchedSteps[0]}>
            {getLocalizedText('الاسم الثاني', 'Second Name')}
          </FieldLabel>
          <input
            type="text"
            name="second_name"
            className="tu-input"
            value={formData.second_name}
            onChange={handleInput}
            maxLength={11}
            placeholder={getLocalizedText('الاسم الثاني', 'Second name')}
          />
        </div>
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('gender')} touched={touchedSteps[0]}>
          {getLocalizedText('النوع', 'Gender')}
        </FieldLabel>
        <div className="tu-inline-choices">
          {genderOptions.map((opt) => (
            <button
              type="button"
              key={opt.value}
              className={`tu-choice-card tu-gender-card ${formData.gender === opt.value ? 'is-selected' : ''}`}
              onClick={() => setField('gender', opt.value)}
            >
              <img src={opt.icon} alt={opt.en} className="tu-choice-icon tu-gender-icon" />
              <span>{getLocalizedText(opt.ar, opt.en)}</span>
            </button>
          ))}
        </div>
      </div>


      <FieldLabel valid={isFieldValid('profile_photo')} touched={touchedSteps[0]}>
        {getLocalizedText('صورة الملف الشخصي', 'Profile Photo')}
      </FieldLabel>

      <div
        className={`tu-upload-zone ${isDragging ? 'is-dragging' : ''} ${formData.profile_photo_preview ? 'has-image' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !formData.profile_photo_preview && fileInputRef.current?.click()}
        
      >
        {formData.profile_photo_preview ? (
          <>
            <img src={formData.profile_photo_preview} alt="preview" className="tu-upload-preview" />
            <button type="button" className="tu-upload-remove" onClick={(e) => { e.stopPropagation(); handleRemovePhoto(); }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCloudUploadAlt} className="tu-upload-icon" />
            <span className="tu-upload-title">{getLocalizedText('ارفع صورتك الشخصية', 'Upload your profile photo')}</span>
            <span className="tu-upload-hint">
              {getLocalizedText('اسحب الصورة هنا أو اضغط للاختيار — JPG أو PNG', 'Drag & drop or click to browse — JPG or PNG')}
            </span>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoChange}
          className="tu-hidden-input"
        />
      </div>
      {compressionStatus && (
        <div className="tu-compressing">{getLocalizedText('جاري ضغط الصورة...', 'Compressing image...')}</div>
      )}

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('age')} touched={touchedSteps[0]}>
          {getLocalizedText('العمر', 'Age')}
        </FieldLabel>
        <AgePicker
          value={formData.age}
          onSelect={(ageValue) => setField('age', ageValue)}
          getLocalizedText={getLocalizedText}
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('bio')} touched={touchedSteps[0]}>
          {getLocalizedText('عن نفسك', 'About You')}
        </FieldLabel>
        <textarea
          name="bio"
          className="tu-textarea"
          value={formData.bio}
          onChange={handleInput}
          placeholder={getLocalizedText(
            'عرّف زملاءك المستقبليين بنفسك. احكِلهم عن دراستك أو شغلك، شخصيتك، هواياتك، أسلوب حياتك، وأي حاجة تساعدهم يتعرفوا عليك أكتر.',
            'Introduce yourself to your future roommates. Tell them about your studies or work, your personality, hobbies, lifestyle, and anything that helps them get to know you.'
          )}
        />
        {formData.bio.trim().length > 0 && formData.bio.trim().length < 20 && (
          <div className="tu-error-text">
            {getLocalizedText('اكتب على الأقل 20 حرف', 'Please write at least 20 characters')}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamStep(p) {
  const { getLocalizedText, isFieldValid, touchedSteps, formData, handleInput, setField } = p;

  return (
    <div className="tu-step-body">
     

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('desired_team_size')} touched={touchedSteps[1]}>
          {getLocalizedText('أقصى حجم للفريق (بما في ذلك أنت)', 'Maximum Team Size (including you)')}
        </FieldLabel>
        <OptionPicker
          value={formData.desired_team_size}
          options={desiredTeamSizeOptions}
          onSelect={(val) => setField('desired_team_size', val)}
          getLocalizedText={getLocalizedText}
          placeholder={getLocalizedText('اختر العدد', 'Select number')}
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('current_team_size')} touched={touchedSteps[1]}>
          {getLocalizedText('حجم الفريق الحالي (بما في ذلك أنت)', 'Current Team Size (including you)')}
        </FieldLabel>
        <OptionPicker
          value={formData.current_team_size}
          options={currentTeamSizeOptions}
          onSelect={(val) => setField('current_team_size', val)}
          getLocalizedText={getLocalizedText}
          placeholder={getLocalizedText('اختر العدد', 'Select number')}
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('has_flat')} touched={touchedSteps[1]}>
          {getLocalizedText('هل عثرتَ بالفعل على شقة للإيجار؟', 'Did you already found a flat to rent?')}
        </FieldLabel>
        <div className="tu-inline-choices">
          <button
            type="button"
            className={`tu-choice-card tu-gender-card ${formData.has_flat === 'yes' ? 'is-selected' : ''}`}
            onClick={() => setField('has_flat', 'yes')}
          >
            <img src={foundHouseIcon} alt="Yes" className="tu-choice-icon tu-gender-icon" />
            <span>{getLocalizedText('نعم، لقد عثرتُ على شقة.', 'Yes, I found a flat.')}</span>
          </button>
          <button
            type="button"
            className={`tu-choice-card tu-gender-card ${formData.has_flat === 'no' ? 'is-selected' : ''}`}
            onClick={() => setField('has_flat', 'no')}
          >
            <img src={searchingIcon} alt="No" className="tu-choice-icon tu-gender-icon" />
            <span>{getLocalizedText('لا، لم أعثر على شقة بعد.', 'No, I did not find a flat yet.')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}


function FlatDetailsStep(p) {
  const { getLocalizedText, isFieldValid, touchedSteps, formData, handleInput, setField,
    isCityDropdownOpen, setIsCityDropdownOpen, isRegionDropdownOpen, setIsRegionDropdownOpen,
    citySelectorRef, regionSelectorRef, handleCitySelect, handleRegionSelect,
    isDragging, setIsDragging, flatPhotosInputRef, flatPhotosCompressing,
    handleFlatPhotosChange, handleFlatPhotosDrop, handleRemoveFlatPhoto, MAX_FLAT_PHOTOS } = p;

  return (
    <div className="tu-step-body">

      <LocationPicker
        label={getLocalizedText('الموقع', 'Location')}
        cityField="city"
        regionField="region"
        formData={formData}
        isFieldValid={isFieldValid}
        touched={touchedSteps[2]}
        isCityDropdownOpen={isCityDropdownOpen}
        setIsCityDropdownOpen={setIsCityDropdownOpen}
        isRegionDropdownOpen={isRegionDropdownOpen}
        setIsRegionDropdownOpen={setIsRegionDropdownOpen}
        citySelectorRef={citySelectorRef}
        regionSelectorRef={regionSelectorRef}
        handleCitySelect={handleCitySelect}
        handleRegionSelect={handleRegionSelect}
        getLocalizedText={getLocalizedText}
      />

      <div className="tu-field-row">
        <div className="tu-field">
          <FieldLabel valid={isFieldValid('bedrooms')} touched={touchedSteps[2]}>
            <FontAwesomeIcon icon={faBed} className="tu-field-icon" /> {getLocalizedText('عدد الغرف', 'Bedrooms')}
          </FieldLabel>
          <OptionPicker
            value={formData.bedrooms}
            options={bedroomsOptions}
            onSelect={(val) => setField('bedrooms', val)}
            getLocalizedText={getLocalizedText}
            placeholder={getLocalizedText('اختر عدد الغرف', 'Select number of bedrooms')}
          />
        </div>
        <div className="tu-field">
          <FieldLabel valid={isFieldValid('bathrooms')} touched={touchedSteps[2]}>
            <FontAwesomeIcon icon={faBath} className="tu-field-icon" /> {getLocalizedText('عدد الحمامات', 'Bathrooms')}
          </FieldLabel>
          <OptionPicker
            value={formData.bathrooms}
            options={bathroomsOptions}
            onSelect={(val) => setField('bathrooms', val)}
            getLocalizedText={getLocalizedText}
            placeholder={getLocalizedText('اختر عدد الحمامات', 'Select number of bathrooms')}
          />
        </div>
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('monthly_rent')} touched={touchedSteps[2]}>
          <FontAwesomeIcon icon={faCoins} className="tu-field-icon" /> {getLocalizedText('الإيجار الشهري للشقة', 'Monthly Rent for the Flat')}
        </FieldLabel>
        <input type="text" inputMode="numeric" name="monthly_rent" className="tu-input" value={formData.monthly_rent} onChange={handleInput} placeholder="EGP" />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('refundable_insurance')} touched={touchedSteps[2]}>
          <FontAwesomeIcon icon={faCoins} className="tu-field-icon" /> {getLocalizedText('التأمين القابل للاسترداد', 'Refundable Insurance')}
        </FieldLabel>
        <input type="text" inputMode="numeric" name="refundable_insurance" className="tu-input" value={formData.refundable_insurance} onChange={handleInput} placeholder="EGP" />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('move_in_date')} touched={touchedSteps[2]}>
          <FontAwesomeIcon icon={faCalendarAlt} className="tu-field-icon" /> {getLocalizedText('جاهز للإنتقال من', 'Ready to Move-in Date')}
        </FieldLabel>
        <DateField
          value={formData.move_in_date}
          onSelect={(iso) => setField('move_in_date', iso)}
          getLocalizedText={getLocalizedText}
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('flat_description')} touched={touchedSteps[2]}>
          {getLocalizedText('أخبرنا عن الشقة التي عثرت عليها.', 'Tell us about the flat you have found')}
        </FieldLabel>
        <textarea
          name="flat_description"
          className="tu-textarea"
          rows={4}
          value={formData.flat_description}
          onChange={handleInput}
          placeholder={getLocalizedText('اكتب وصف مختصر (٢٠ حرف على الأقل)', 'Write a short description (min 20 characters)')}
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid touched={false}>
          {getLocalizedText('صور الشقة (اختياري)', 'Flat Photos (optional)')}
        </FieldLabel>
        <div className="tu-flat-photos-grid">
          {formData.flat_photos_previews.map((src, index) => (
            <div key={index} className="tu-flat-photo-thumb">
              <img src={src} alt={`flat-${index}`} />
              <button
                type="button"
                className="tu-upload-remove tu-flat-photo-remove"
                onClick={() => handleRemoveFlatPhoto(index)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
          {formData.flat_photos_previews.length < MAX_FLAT_PHOTOS && (
            <div
              className={`tu-upload-zone tu-flat-photo-add ${isDragging ? 'is-dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleFlatPhotosDrop}
              onClick={() => flatPhotosInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={faCloudUploadAlt} className="tu-upload-icon" />
              <span className="tu-upload-hint">
                {getLocalizedText('ارفع صور', 'Add photos')}
              </span>
              <input
                ref={flatPhotosInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFlatPhotosChange}
                className="tu-hidden-input"
              />
            </div>
          )}
        </div>
        <div className="tu-select-hint">
          {getLocalizedText(
            `يمكنك رفع حتى ${MAX_FLAT_PHOTOS} صور، إذا كان لديك صور للمكان.`,
            `If you have photos of the place, you can upload up to ${MAX_FLAT_PHOTOS}.`
          )}
        </div>
        {flatPhotosCompressing && (
          <div className="tu-compressing">{getLocalizedText('جاري ضغط الصور...', 'Compressing images...')}</div>
        )}
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('open_to_other_flat')} touched={touchedSteps[2]}>
          {getLocalizedText('هل أنت منفتح للبحث عن شقة اخرى؟', 'Are you open to searching for another apartment?')}
        </FieldLabel>
        <div className="tu-inline-choices">
          <ChoiceCard selected={formData.open_to_other_flat === 'yes'} onClick={() => setField('open_to_other_flat', 'yes')}>
            {getLocalizedText('نعم', 'Yes')}
          </ChoiceCard>
          <ChoiceCard selected={formData.open_to_other_flat === 'no'} onClick={() => setField('open_to_other_flat', 'no')}>
            {getLocalizedText('لا', 'No')}
          </ChoiceCard>
        </div>
      </div>
    </div>
  );
}

function LookingForStep(p) {
  const { getLocalizedText, isFieldValid, touchedSteps, formData, handleInput, setField,
    isCityDropdownOpen, setIsCityDropdownOpen, isRegionDropdownOpen, setIsRegionDropdownOpen,
    citySelectorRef, regionSelectorRef, handleCitySelect, handleRegionToggle } = p;

  return (
    <div className="tu-step-body">

      <LocationPicker
        label={getLocalizedText('الموقع المفضل', 'Preferred Location')}
        cityField="preferred_city"
        regionField="preferred_regions"
        regionMultiSelect
        maxRegions={4}
        formData={formData}
        isFieldValid={isFieldValid}
        touched={touchedSteps[2]}
        isCityDropdownOpen={isCityDropdownOpen}
        setIsCityDropdownOpen={setIsCityDropdownOpen}
        isRegionDropdownOpen={isRegionDropdownOpen}
        setIsRegionDropdownOpen={setIsRegionDropdownOpen}
        citySelectorRef={citySelectorRef}
        regionSelectorRef={regionSelectorRef}
        handleCitySelect={handleCitySelect}
        handleRegionToggle={handleRegionToggle}
        getLocalizedText={getLocalizedText}
      />

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('budget_per_person')} touched={touchedSteps[2]}>
          <FontAwesomeIcon icon={faCoins} className="tu-field-icon" /> {getLocalizedText('الميزانية للفرد', 'Budget per Member')}
        </FieldLabel>
        <input type="text" inputMode="numeric" name="budget_per_person" className="tu-input" value={formData.budget_per_person} onChange={handleInput} placeholder="EGP" />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('preferred_move_in_date')} touched={touchedSteps[2]}>
          <FontAwesomeIcon icon={faCalendarAlt} className="tu-field-icon" /> {getLocalizedText('جاهز للإنتقال من', 'Ready to Move-in Date')}
        </FieldLabel>
        <DateField
          value={formData.preferred_move_in_date}
          onSelect={(iso) => setField('preferred_move_in_date', iso)}
          getLocalizedText={getLocalizedText}
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('looking_for_description')} touched={touchedSteps[2]}>
          {getLocalizedText('صف الشقة التي تتطلع إلى السكن فيها', "Describe the Flat you're looking forward to live in")}
        </FieldLabel>
        <textarea
          name="looking_for_description"
          className="tu-textarea"
          rows={4}
          value={formData.looking_for_description}
          onChange={handleInput}
          placeholder={getLocalizedText('اكتب الوصف (٢٠ حرف على الأقل)', 'Write the description (min 20 characters)')}
        />
      </div>
    </div>
  );
}

function LifestyleStep(p) {
  const { getLocalizedText, formData, toggleLifestyle, touchedSteps } = p;

  return (
    <div className="tu-step-body">
      <h2 className="tu-step-title">{getLocalizedText('نمط الحياة وقواعد المنزل', 'Lifestyle & House Rules')}</h2>
      <div className="tu-lifestyle-grid">
        {lifestyleOptions.map((opt) => {
          const selected = formData.lifestyle.includes(opt.value);
          return (
            <button
              type="button"
              key={opt.value}
              className={`tu-lifestyle-card ${selected ? 'is-selected' : ''}`}
              onClick={() => toggleLifestyle(opt.value)}
            >
              <div className="tu-lifestyle-icon-wrap">
                <img src={opt.icon} alt={opt.en} className="tu-lifestyle-icon" />
              </div>
              <span>{getLocalizedText(opt.ar, opt.en)}</span>
              {selected && <FontAwesomeIcon icon={faCheck} className="tu-lifestyle-check" />}
            </button>
          );
        })}
      </div>
      {touchedSteps[3] && formData.lifestyle.length === 0 && (
        <div className="tu-error-text">{getLocalizedText('اختار عنصر واحد على الأقل', 'Select at least one option')}</div>
      )}
    </div>
  );
}

function ContactStep(p) {
  const { getLocalizedText, isFieldValid, touchedSteps, formData, handleInput } = p;

  return (
    <div className="tu-step-body">

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('contact_phone')} touched={touchedSteps[4]}>
          <FontAwesomeIcon icon={faPhone} className="tu-field-icon" /> {getLocalizedText('رقم الهاتف', 'Phone Number')}
        </FieldLabel>
        <input
          type="tel"
          name="contact_phone"
          className="tu-input"
          value={formData.contact_phone}
          onChange={handleInput}
          placeholder={getLocalizedText('01xxxxxxxxx :مثال', 'e.g. 01xxxxxxxxx')}
          maxLength="11"
        />
      </div>

      <div className="tu-field">
        <FieldLabel valid={isFieldValid('contact_whatsapp')} touched={touchedSteps[4]}>
          <FontAwesomeIcon icon={faWhatsapp} className="tu-field-icon" /> {getLocalizedText('رقم الواتساب', 'WhatsApp Number')}
        </FieldLabel>
        <input
          type="tel"
          name="contact_whatsapp"
          className="tu-input"
          value={formData.contact_whatsapp}
          onChange={handleInput}
          placeholder={getLocalizedText('01xxxxxxxxx :مثال', 'e.g. 01xxxxxxxxx')}
          maxLength="11"
        />
      </div>
    </div>
  );
}

// ─── Public component: picks the right step body based on `step` ──────────
// (step 2 depends on `flatChosen` — same behaviour as the original
// `stepRenderers` array in the main component.)

function TeamUpStepContent(props) {
  const { step, flatChosen } = props;

  switch (step) {
    case 0:
      return <AboutStep {...props} />;
    case 1:
      return <TeamStep {...props} />;
    case 2:
      return flatChosen ? <FlatDetailsStep {...props} /> : <LookingForStep {...props} />;
    case 3:
      return <LifestyleStep {...props} />;
    case 4:
      return <ContactStep {...props} />;
    default:
      return null;
  }
}

export default TeamUpStepContent;